'use client';

import { SvgMagnifyingGlass, SvgTextfieldclose } from '@common/icon';
import { cn } from '@core/utils';
import { usePathname } from 'next/navigation';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';

import { recentHistory } from '@/app/donation/gifts/search/_utils/recent-history';
import { DONATION_ROUTES } from '@/constants';
import { useCustomRouter } from '@/hooks/useCustomRouter';

import { DropdownContent } from './DropdownContent';

interface SearchProps {
  placeholder?: string;
  className?: string;
}

export const Search = ({
  placeholder = '찾으시는 답례품을 입력해 주세요.',
  className,
}: SearchProps) => {
  const pathname = usePathname();
  const router = useCustomRouter();
  const { add } = recentHistory;

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleSearch = (searchKeyword: string) => {
    if (searchKeyword.trim().length === 0) {
      router.push(DONATION_ROUTES.GIFTS.pathname);

      return;
    }

    add(searchKeyword);

    const isReplace = pathname === DONATION_ROUTES.GIFTS_SEARCH.pathname;

    router[isReplace ? 'replace' : 'push'](
      DONATION_ROUTES.GIFTS_SEARCH.withSearchParams({
        searchParams: {
          searchKeyword,
        },
      }),
    );

    setIsOpen(false);
    handleClear();
  };

  const handleClear = () => {
    setSearchTerm('');
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
      handleSearch(searchTerm);
    }
  };

  return (
    <div className={cn('relative', className)}>
      <div className="relative w-[352px]">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setIsOpen(true)}
          onBlur={() => {
            //* 드롭다운 내부 클릭 시 닫히지 않도록 약간의 지연 추가
            setTimeout(() => setIsOpen(false), 150);
          }}
          placeholder={placeholder}
          className={cn(
            'w-full rounded-small bg-Gray-25 py-12 pl-20 pr-70 text-Gray-800 typo-body3 focus:outline-none',
            {
              'bg-Gray-white border-1 border-Gray-black': isOpen,
            },
          )}
        />
        {searchTerm.length > 0 ? (
          <button
            type="button"
            aria-label="검색어 삭제"
            onClick={handleClear}
            className="absolute right-40 top-1/2 -translate-y-1/2 p-8"
          >
            <SvgTextfieldclose className="size-16 text-Gray-400" />
          </button>
        ) : null}
        <button
          type="button"
          aria-label="검색"
          onClick={() => handleSearch(searchTerm)}
          className="absolute right-12 top-1/2 -translate-y-1/2 p-8"
        >
          <SvgMagnifyingGlass className="size-20 text-Gray-black" />
        </button>
      </div>

      {/* 드롭다운 콘텐츠 */}
      {isOpen && <DropdownContent onKeywordClick={handleSearch} />}
    </div>
  );
};
